import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useMemo } from 'react';
import {
  FormattedDate,
  FormattedMessage,
  FormattedNumber,
  IntlShape,
  useIntl,
} from 'react-intl';
import { useHistory, useLocation } from 'react-router-dom';
import Box, { BoxProps } from 'ui-box';
import {
  Button,
  defaultFills,
  majorScale,
  Menu,
  MenuDivider,
  MenuItem,
  minorScale,
  Popover,
  Section,
  Spinner,
  Table,
  Tag,
  useAlert,
  useToast,
} from '../../npaper';
import { SendItemSideSheet } from '../../npaper/SendItemSideSheet';
import { ISale, useStores } from '../../stores';
import { CreditNoteReason, ICreditNote } from '../../stores/CreditNoteStore';
import { IQueryCache } from '../../stores/utils/modelWithCRUD';
import { CreditNoteSideSheet } from './CreditNoteDialog';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export const CreditNoteIcon = ({
  width = '14px',
  height = '16px',
  color = defaultFills.normal[500],
  ...props
}: {
  width?: string | number;
  height?: string | number;
  color?: string;
} & BoxProps<'svg'>) => (
  <Box
    {...props}
    is="svg"
    width={width}
    height={height}
    viewBox="0 0 14 16"
    version="1.1"
    xmlns="http://www.w3.org/2000/svg"
  >
    <g id="Salg" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
      <g id="Group" transform="translate(-3.000000, -2.000000)" fill={color}>
        <g id="Shape" transform="translate(-0.000000, -0.000000)">
          <path d="M5.00000001,2.00000001 C3.89543002,2.00000001 3.00000001,2.89543001 3.00000001,4.00000001 L3.00000001,18 L6.50000001,16 L10,18 L13.5,16 L17,18 L17,4.00000001 C17,2.89543001 16.1046001,2.00000001 15,2.00000001 L5.00000001,2.00000001 Z M9.70711005,5.70711003 C10.0976001,5.31658003 10.0976001,4.68342002 9.70711005,4.29289002 C9.31658005,3.90237002 8.68342004,3.90237002 8.29289004,4.29289002 L5.29289003,7.29289004 C4.90237002,7.68342004 4.90237002,8.31658004 5.29289003,8.70711004 L8.29289004,11.7071001 C8.68342004,12.0976001 9.31658005,12.0976001 9.70711005,11.7071001 C10.0976001,11.3166001 10.0976001,10.6834001 9.70711005,10.2929001 L8.41421004,9.00000002 L10,9.00000002 C11.6569001,9.00000002 13,10.3431001 13,12 L13,13 C13,13.5523001 13.4477001,14 14,14 C14.5523001,14 15,13.5523001 15,13 L15,12 C15,9.23858005 12.7614001,7.00000002 10,7.00000002 L8.41421004,7.00000002 L9.70711005,5.70711003 Z"></path>
        </g>
      </g>
    </g>
  </Box>
);

export const formatCreditNoteReason = (
  intl: IntlShape,
  reason: CreditNoteReason
) => {
  switch (reason) {
    case CreditNoteReason.ORDER_CHANGE:
      return intl.formatMessage({
        defaultMessage: 'Order change',
      });
    case CreditNoteReason.OTHER:
      return intl.formatMessage({
        defaultMessage: 'Other',
      });
    case CreditNoteReason.UNAVAILABLE:
      return intl.formatMessage({
        defaultMessage: 'Unavailable product or service',
      });
    case CreditNoteReason.UNSATISFACTORY:
      return intl.formatMessage({
        defaultMessage: 'Unsatisfactory product or service',
      });
  }
};

export const CreditNotesEmptyPlaceholder: React.FC<BoxProps<'div'>> = (
  props
) => (
  <Box textAlign="center" paddingY={majorScale(2)} {...props}>
    <svg
      width="36px"
      height="40px"
      viewBox="0 0 18 20"
      version="1.1"
      xmlns="http://www.w3.org/2000/svg"
      className="mx-auto"
    >
      <g
        id="Salg"
        stroke="none"
        strokeWidth="1"
        fill="none"
        fillRule="evenodd"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <g
          id="icons/outline/icon-receipt-refund"
          transform="translate(-3.000000, -2.000000)"
        >
          <g
            id="Group"
            transform="translate(-0.000000, -0.000000)"
            stroke={defaultFills.muted}
            strokeWidth="2"
          >
            <path
              d="M16,15 L16,14 C16,11.7909 14.2091001,10 12,10 L8.00000003,10 M8.00000003,10 L11,13 M8.00000003,10 L11,7.00000003 M20,21 L20,5.00000001 C20,3.89543002 19.1046001,3.00000001 18,3.00000001 L6.00000002,3.00000001 C4.89543002,3.00000001 4.00000002,3.89543002 4.00000002,5.00000001 L4.00000002,21 L8.00000002,19 L12,21 L16,19 L20,21 Z"
              id="Shape"
            ></path>
          </g>
        </g>
      </g>
    </svg>
    <p className="medium mt-2">
      <FormattedMessage defaultMessage="No credit notes" />
    </p>
    <p className="muted">
      <FormattedMessage defaultMessage="When you add a credit note to your invoice it will show up here" />
    </p>
  </Box>
);

export const CreditNotesTable = observer(
  ({
    cache,
    onDelete,
    onInspect,
  }: {
    cache?: IQueryCache<ICreditNote>;
    onDelete: (creditNote: ICreditNote) => void;
    onInspect: (creditNote: ICreditNote) => void;
  }) => {
    const intl = useIntl();

    if (!cache || cache.isLoading) {
      return <Spinner size={16} />;
    }

    if (cache.items.length === 0) {
      return <CreditNotesEmptyPlaceholder />;
    }
    return (
      <Table>
        <thead>
          <tr>
            <th>
              <FormattedMessage defaultMessage="Number" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Date created" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Date issued" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Reason" />
            </th>
            <th>
              <FormattedMessage defaultMessage="Note" />
            </th>
            <th className="right">
              <FormattedMessage defaultMessage="Sub total" />
            </th>
            <th style={{ width: 40 }}></th>
          </tr>
        </thead>
        <tbody>
          {cache.items.map((item) => (
            <tr key={item.id}>
              <td className="font-mono">
                {item.invoice_number ? (
                  <Box display="flex" alignItems="center">
                    <CreditNoteIcon />{' '}
                    <Box marginLeft={minorScale(2)}>{item.invoice_number}</Box>
                  </Box>
                ) : (
                  <Tag intent="none">
                    <FormattedMessage defaultMessage="Draft" />
                  </Tag>
                )}
              </td>
              <td>
                <FormattedDate format="default" value={item.created_at} />
              </td>
              <td>
                {item.date_issued ? (
                  <FormattedDate format="default" value={item.date_issued} />
                ) : (
                  <span className="muted">
                    <FormattedMessage defaultMessage="Not issued" />
                  </span>
                )}
              </td>
              <td>{formatCreditNoteReason(intl, item.reason)}</td>
              <td>
                <Box
                  is="p"
                  maxWidth="300px"
                  className="elipsis"
                  title={item.note || ''}
                >
                  {item.note}
                </Box>
              </td>
              <td className="font-mono right">
                <FormattedNumber
                  format="currency"
                  currency={item.currency}
                  value={item.sub_total}
                />
              </td>
              <td className="right">
                <Popover
                  content={(close) => (
                    <Menu>
                      {item.is_issued ? (
                        <>
                          <MenuItem
                            icon="send-message"
                            text={intl.formatMessage({
                              defaultMessage: 'Send credit note',
                            })}
                            onClick={() => {
                              close();
                              onInspect(item);
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <MenuItem
                            icon="edit"
                            text={intl.formatMessage({
                              defaultMessage: 'Edit',
                            })}
                            onClick={() => {
                              close();
                              onInspect(item);
                            }}
                          />
                          <MenuDivider />
                          <MenuItem
                            icon="trash"
                            intent="danger"
                            text={intl.formatMessage({
                              defaultMessage: 'Delete draft',
                            })}
                            onClick={() => {
                              close();
                              onDelete(item);
                            }}
                          />
                        </>
                      )}
                    </Menu>
                  )}
                >
                  <Button minimal iconLeft="more" />
                </Popover>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }
);

export const CreditNotesSection = ({ sale }: { sale: ISale }) => {
  const [creatingCreditNote, setCreatingCreditNote] = useState(false);
  const [inspectCreditNote, setInspectCreditNote] = useState<ICreditNote>();

  const toaster = useToast();
  const intl = useIntl();

  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const { creditNoteStore, saleStore } = useStores();

  useEffect(() => {
    let isSub = true;
    if (searchParams.get('credit_note')) {
      let creditNoteId = searchParams.get('credit_note');
      creditNoteStore
        .getItem(creditNoteId)
        .then((cn) => isSub && setInspectCreditNote(cn));
    }
    return () => {
      isSub = false;
    };
  }, [creditNoteStore, searchParams]);

  const [cache, setCache] = useState<IQueryCache<ICreditNote>>();

  const notCreditableAlert = useAlert({
    title: intl.formatMessage({
      defaultMessage: 'Invoice is not creditable',
      description: 'Invoice not creditable alert title',
    }),
    message: intl.formatMessage({
      defaultMessage:
        'This invoice is not creditable. This is most likely because it has already been fully creditted.',
      description: 'Invoice not creditable alert message',
    }),
    icon: 'info-sign',
    intent: 'info',
    hideConfirm: true,
  });

  useEffect(() => {
    let isSub = true;
    creditNoteStore.getAll({ filter: { sale: sale.id } }).then((cache) => {
      isSub && setCache(cache);
    });
    return () => {
      isSub = false;
    };
  }, [creditNoteStore, sale.id]);

  return (
    <Section
      title={intl.formatMessage({
        defaultMessage: 'Credit notes',
        description: 'Credit notes heading',
      })}
      actions={
        <>
          <Button
            minimal={true}
            iconRight="add"
            isLoading={creatingCreditNote}
            onClick={async () => {
              if (!sale.creditable) {
                notCreditableAlert.show();
                return;
              }
              setCreatingCreditNote(true);
              try {
                const creditNote = await creditNoteStore.createItem({
                  sale: sale.id,
                });
                setInspectCreditNote(creditNote);
              } catch (err) {
                toaster.showDefaultError(err);
              }

              setCreatingCreditNote(false);
            }}
          >
            <FormattedMessage defaultMessage="Create credit note" />
          </Button>
        </>
      }
    >
      {inspectCreditNote &&
        (inspectCreditNote.is_issued ? (
          <SendItemSideSheet
            isShown={true}
            onClose={() => setInspectCreditNote(undefined)}
            pdfUrl={`${creditNoteStore.apiUrl}${creditNoteStore.apiPath}/${inspectCreditNote.id}/pdf/`}
            title={intl.formatMessage({
              defaultMessage: 'Send credit note',
            })}
            sendTitle={intl.formatMessage({
              defaultMessage: 'Send credit note',
            })}
            downloadTitle={intl.formatMessage({
              defaultMessage: 'Download credit note',
            })}
            filename={() => `cn-${inspectCreditNote.invoice_number}.pdf`}
            onDownload={async (language) => {
              if (sale.language !== language) {
                await saleStore.updateItem(sale, { language });
              }
            }}
            onSubmit={async (values) => {
              try {
                await creditNoteStore.sendCreditNote(
                  inspectCreditNote,
                  values.recipients,
                  values.language,
                  values.addContactsCC,
                  values.addResponsibleBCC
                );
                toaster.addToast({
                  intent: 'success',
                  message: intl.formatMessage({
                    defaultMessage:
                      'Credit note successfully sent to recipients',
                  }),
                });
                setInspectCreditNote(undefined);
              } catch (error) {
                toaster.showDefaultError(error);
              }
            }}
            recipientCompany={sale.customer}
            defaultValue={{
              // TODO: Refactor this
              recipients: inspectCreditNote.sale &&
                (inspectCreditNote.sale as ISale).customer &&
                (inspectCreditNote.sale as ISale).customer.email && [
                  (inspectCreditNote.sale as ISale).customer.email,
                ],
            }}
          />
        ) : (
          <CreditNoteSideSheet
            isShown={!!inspectCreditNote}
            onClose={() => setInspectCreditNote(undefined)}
            creditNote={inspectCreditNote}
          />
        ))}

      <CreditNotesTable
        cache={cache}
        onInspect={setInspectCreditNote}
        onDelete={(creditNote) => {
          creditNoteStore
            .deleteItem(creditNote)
            .catch(toaster.showDefaultError);
        }}
      />
    </Section>
  );
};

export const CreditNotesBadge = ({ sale }: { sale: ISale }) => {
  const history = useHistory();
  const intl = useIntl();
  return (
    <Popover
      content={(close) => (
        <Menu>
          {sale.credit_notes.map((creditNote: ICreditNote | undefined, i) => (
            <MenuItem
              key={creditNote ? creditNote.id : `index.${i}`}
              onClick={() => {
                if (creditNote) {
                  close();
                  history.push(
                    `/sales/${sale.id}?credit_note=${creditNote.id}`
                  );
                }
              }}
              paddingRight={'8px !important'}
              text={
                creditNote ? (
                  <Box display="flex" alignItems="center">
                    {creditNote.is_issued ? (
                      <CreditNoteIcon />
                    ) : (
                      <Tag>
                        <FormattedMessage defaultMessage="Draft" />
                      </Tag>
                    )}
                    <Box marginLeft={minorScale(2)}>
                      <Box display="flex" justifyContent="space-between">
                        <p>{formatCreditNoteReason(intl, creditNote.reason)}</p>
                        <p className="font-mono pl-2">
                          <FormattedNumber
                            format="currency"
                            currency={creditNote.currency}
                            value={creditNote.sub_total}
                          />
                        </p>
                      </Box>
                      {creditNote.date_issued && (
                        <p className="muted small">
                          <FormattedDate
                            format="default"
                            value={creditNote.date_issued}
                          />
                        </p>
                      )}
                      <Box
                        is="p"
                        className="muted small elipsis"
                        maxWidth="300px"
                      >
                        {creditNote.note}
                      </Box>
                    </Box>
                  </Box>
                ) : (
                  <Spinner size={16} />
                )
              }
            />
          ))}
        </Menu>
      )}
    >
      <Button
        iconLeft={<CreditNoteIcon />}
        small
        minimal
        marginLeft={minorScale(1)}
      />
    </Popover>
  );
};
